import { ApolloError, Operation } from "@apollo/client";
import { NetworkError } from "@apollo/client/errors";
import * as Sentry from "@sentry/react";
import { toLower } from "lodash";

import { errorNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";

export type ErrorMatchType = {
  match: string;
  title: string;
  description?: string;
};

const anUnexpectedErrorOccurred = "An unexpected error occurred.";

export const defaultNetworkErrorHandler = (
  networkError: NetworkError,
  operation?: Operation,
) => {
  if (!networkError) return;

  if ("statusCode" in networkError && networkError.statusCode === 401) {
    const loginUrl = `/login?next=${window.location.pathname}`;
    errorNotificationVar({
      timeout: null,
      title: "Your session has expired.",
      description: (
        <div>
          Please refresh the page and{" "}
          <a href={loginUrl} className="underline text-blue-600">
            log in
          </a>
          .
          <div className="mt-4">
            <Button url={loginUrl} theme={buttonTheme.primary} text="Login" />
          </div>
        </div>
      ),
    });
  } else if (toLower(String(networkError)).includes("failed to fetch")) {
    errorNotificationVar({
      description: "Make sure you are connected to the internet and try again.",
    });
  } else {
    errorNotificationVar({
      description: `An unexpected error occurred. We have been notified. Please try again.${
        window.Cypress ? JSON.stringify(networkError) : ""
      }`,
    });
    Sentry.captureException(
      new Error(
        `[Network error] ${operation?.operationName || ""}: ${networkError}`,
      ),
      {
        extra: {
          operationName: operation?.operationName,
          variables: operation?.variables,
          error: networkError,
        },
      },
    );
  }
};

export const onNotificationErrorHandler = (
  errorMatches: Array<ErrorMatchType>,
) => {
  return function (this: any, error: ApolloError) {
    if (error.networkError) {
      defaultNetworkErrorHandler(error.networkError);
      return;
    }

    // handle notification custom errors
    const errorMatch = errorMatches.find(({ match }) =>
      String(error).toLocaleLowerCase().includes(match.toLocaleLowerCase()),
    );
    if (errorMatch) {
      errorNotificationVar(errorMatch);
      return;
    }

    // throw error if does not have any matches
    errorNotificationVar({
      title: anUnexpectedErrorOccurred,
      description: "We have been notified. Please try again.",
    });

    if (error.graphQLErrors) {
      error.graphQLErrors.forEach((graphQLError) => {
        Sentry.captureException(
          `[GraphQL Error] ${(graphQLError.path || []).join(" > ")}: ${
            graphQLError.message
          }`,
          {
            extra: {
              error: graphQLError,
            },
          },
        );
      });
    } else {
      Sentry.captureException(new Error(error.message), {
        extra: { error },
      });
    }
  };
};
